import { ApiError, apiFetch } from './http';
import { buildPortalApiPath } from './routes';

interface VoucherResponse {
  ok: boolean;
  result_id: string;
  correlation_id: string;
}

export interface VoucherDocumentUpload {
  fieldId: string;
  fileName: string;
  uploadUrl?: string;
  headers?: Record<string, string>;
  mock?: boolean;
}

export interface VoucherDocumentsResponse extends VoucherResponse {
  uploads: VoucherDocumentUpload[];
}

export class VoucherApiError extends ApiError {
  fieldErrors?: Record<string, string>;

  constructor(message: string, status: number, fieldErrors?: Record<string, string>) {
    super(message, status);
    this.fieldErrors = fieldErrors;
  }
}

// The backend's message when a voucher was submitted but could not be recorded; users must see it
// so they contact support instead of submitting a duplicate.
export const VOUCHER_NOT_RECORDED_ERROR = 'Voucher submission could not be recorded. Contact support before retrying.';

async function voucherRequest<T>(path: `/${string}`, body: object): Promise<T> {
  const response = await apiFetch(buildPortalApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as {
      error?: string;
      fieldErrors?: Record<string, string>;
    };
    throw new VoucherApiError(payload.error ?? 'Voucher request failed', response.status, payload.fieldErrors);
  }
  return response.json() as Promise<T>;
}

export function validateVoucherEligibility(productKey: string, values: Record<string, unknown>) {
  return voucherRequest<{ eligible: boolean }>(
    `/voucher-submissions/products/${encodeURIComponent(productKey)}/validate-eligibility`,
    { values },
  );
}

export async function submitVoucherSubmission(productKey: string, values: Record<string, unknown>) {
  const response = await voucherRequest<VoucherResponse>(
    `/voucher-submissions/products/${encodeURIComponent(productKey)}/submit`,
    { values },
  );
  if (typeof response.result_id !== 'string' || response.result_id.trim() === '') {
    throw new VoucherApiError(VOUCHER_NOT_RECORDED_ERROR, 503);
  }
  return response;
}

export function submitVoucherDocuments(
  submissionResultId: string,
  values: Record<string, unknown>,
  files: Record<string, File[]>,
) {
  return voucherRequest<VoucherDocumentsResponse>('/voucher-submissions/documents', {
    submissionResultId,
    values,
    files: Object.entries(files).flatMap(([fieldId, selectedFiles]) => selectedFiles.map((file) => ({
      fieldId,
      fileName: file.name,
      fileSizeBytes: file.size,
      contentType: file.type,
    }))),
  });
}

export async function uploadVoucherDocuments(
  uploads: VoucherDocumentUpload[],
  files: Record<string, File[]>,
) {
  await Promise.all(uploads.map(async (upload) => {
    if (upload.mock) return;
    if (!upload.uploadUrl) {
      throw new ApiError(`Upload URL is missing for ${upload.fileName}`, 502);
    }
    const file = files[upload.fieldId]?.find((candidate) => candidate.name === upload.fileName);
    if (!file) {
      throw new ApiError(`Selected file is missing for ${upload.fileName}`, 400);
    }
    const response = await fetch(upload.uploadUrl, {
      method: 'PUT',
      credentials: 'omit',
      headers: upload.headers,
      body: file,
    });
    if (!response.ok) {
      throw new ApiError(`Upload failed for ${upload.fileName}`, response.status);
    }
  }));
}